import { SchmancyElement } from '@schmancy/mixins/index'
import { html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { combineLatest, takeUntil } from 'rxjs'
import { sound } from './sound.service'
// The sub-components this widget renders live inside schmancy, so the consumer's
// auto-import never sees them — import them here so they self-register.
import '@schmancy/form/fields/range/range'
import '@schmancy/primitives/typography/typography'

/**
 * schmancy-sound-control — the sound-preference widget, owned by the library the
 * way <schmancy-theme-controller> owns theme. It binds directly to the `sound`
 * service, which owns the persisted volume/mute configuration and gates every
 * sound through it. The slider's zero position mutes; raising it restores sound.
 * Drop it into any surface — no app-side state, no wiring.
 */
@customElement('schmancy-sound-control')
export default class SchmancySoundControl extends SchmancyElement {
	/** Visible row label. Override to localize — defaults to English. */
	@property() label = 'Sound'

	/** Accessible name for the volume slider. Override to localize — defaults to English. */
	@property() volumeLabel = 'Volume'

	@state() private volume = sound.muted ? 0 : sound.volume

	connectedCallback(): void {
		super.connectedCallback()
		combineLatest([sound.muted$, sound.volume$])
			.pipe(takeUntil(this.disconnecting))
			.subscribe(([muted, volume]) => {
				this.volume = muted ? 0 : volume
			})
	}

	protected render(): unknown {
		return html`
			<div class="flex flex-col">
				<schmancy-typography type="body" token="sm" class="relative z-10">${this.label}</schmancy-typography>
				<schmancy-range
					class="[--schmancy-range-visual-offset-y:-8px]"
					.min=${0}
					.max=${1}
					.step=${0.01}
					.value=${this.volume}
					aria-label=${this.volumeLabel}
					@change=${(e: CustomEvent<{ value: number }>) => {
						sound.setVolume(e.detail.value)
						if (e.detail.value === 0) sound.mute()
						else sound.unmute()
					}}
				></schmancy-range>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-sound-control': SchmancySoundControl
	}
}
